import { PrimitiveObject } from "./PrimitiveObject";
import { Box3, Color as ThreeColor, InstancedMesh, Matrix4, Quaternion as ThreeQuaternion, Sphere, Vector3, } from "three";
import { WorldObjectType } from "../../api";
import { constructMeshThreeMaterial } from "./BaseObject";
export class InstancedMeshObject extends PrimitiveObject {
    constructor(object, objectId, positions, rotations, scales, colors, objectData, pickingManager, mesh) {
        super(object);
        this.object = object;
        this.id = objectId;
        this.mesh = mesh;
        this.pickingManager = pickingManager;
        this.maxInstances = Math.min(positions.length, rotations.length);
        this.objectData = objectData;
        this.positions = positions;
        this.rotations = rotations;
        this.scales = scales;
        this._colors = colors;
        this.resolve();
    }
    get colors() {
        return this._colors;
    }
    set colors(colors) {
        // Detect when colors 'definedness' is being changed. This will trigger the material to update when the
        // per-instance colors are added or removed, but do nothing when the array's 'definedness' doesn't change.
        const noColorsBefore = this._colors === undefined;
        const noColorsAfter = colors === undefined;
        if (noColorsBefore !== noColorsAfter) {
            this.object.material.needsUpdate = true;
        }
        this._colors = colors;
    }
    getBoundingVolume() {
        const box = new Box3().setFromObject(this.object);
        const sphere = box.getBoundingSphere(new Sphere(box.getCenter(new Vector3())));
        return [sphere.center.x, sphere.center.y, sphere.center.z, sphere.radius];
    }
    constructMaterial(material) {
        const threeMaterial = this.object.material;
        this.object.material = constructMeshThreeMaterial(material, threeMaterial.clippingPlanes, this.id, false, true);
    }
    getObjectType() {
        return WorldObjectType.InstancedMesh;
    }
    get isTransparent() {
        return this.object.material.opacity < 1 && this.object.material.transparent;
    }
    resolve() {
        this.updateMatrices(this.positions, this.rotations, this.scales, this.colors);
    }
    /**
     * @param positions
     * @param rotations
     * @param scales
     * @param colors
     */
    updateMatrices(positions, rotations, scales, colors) {
        const SCRATCH_T = new Matrix4();
        const SCRATCH_R = new Matrix4();
        const SCRATCH_S = new Matrix4();
        const SCRATCH_C = new ThreeColor();
        const SCRATCH_Q = new ThreeQuaternion();
        // Select the smallest length from the defined input arrays
        let len = Math.min(positions.length, rotations.length, scales.length);
        if (colors !== undefined)
            len = Math.min(len, colors.length);
        if (len > this.maxInstances) {
            const old = this.object;
            this.object.dispose();
            this.object = new InstancedMesh(old.geometry, old.material, len);
            this.pickingManager.setIdForObject(this.object.id, this.id);
            this.maxInstances = len;
            this.object.position.copy(old.position);
            this.object.quaternion.copy(old.quaternion);
            this.object.scale.copy(old.scale);
            this.object.up.copy(old.up);
        }
        else {
            this.object.count = len;
            this.object.instanceMatrix.needsUpdate = true;
            if (this.object.instanceColor) {
                this.object.instanceColor.needsUpdate = true;
            }
        }
        for (let i = 0; i < len; i += 1) {
            SCRATCH_T.makeTranslation(positions[i].x, positions[i].y, positions[i].z);
            SCRATCH_Q.fromArray([rotations[i].x, rotations[i].y, rotations[i].z, rotations[i].w]);
            SCRATCH_R.makeRotationFromQuaternion(SCRATCH_Q);
            SCRATCH_S.makeScale(scales[i].x, scales[i].y, scales[i].z);
            SCRATCH_T.multiply(SCRATCH_R).multiply(SCRATCH_S);
            this.object.setMatrixAt(i, SCRATCH_T);
            if (colors !== undefined) {
                this.object.setColorAt(i, SCRATCH_C.setRGB(colors[i].r, colors[i].g, colors[i].b));
            }
        }
        this.object.instanceMatrix.needsUpdate = true;
        if (this.object.instanceColor) {
            this.object.instanceColor.needsUpdate = true;
        }
        this.object.computeBoundingSphere();
    }
}
